import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, CarFront, FileText, ClipboardCheck, Mail, FileDown } from "lucide-react";
import VehicleFormModal from "@/components/admin/VehicleFormModal";
import LeadsTab from "@/components/admin/LeadsTab";
import SettingsTab from "@/components/admin/SettingsTab";
import ReviewsTab from "@/components/admin/ReviewsTab";
import CalendarTab from "@/components/admin/CalendarTab";
import InsightsTab from "@/components/admin/InsightsTab";
import ReferralsTab from "@/components/admin/ReferralsTab";
import { fetchVehicles, adminMe, adminStats, deleteVehicle, clearToken, zar, mediaUrl, downloadQuotation, downloadCsv, downloadStockSheet } from "@/api";

const SERVICE_LABELS = { "rent-to-own": "RTO", rental: "Rental", sale: "Sale" };
const TAB_TITLES = {
  vehicles: "Stock & Vehicles", leads: "Leads Inbox", calendar: "Rental Calendar",
  reviews: "Reviews", referrals: "Referral Programme", insights: "Business Insights", settings: "Settings",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null);
  const [tab, setTab] = useState("vehicles");

  const load = useCallback(() => {
    fetchVehicles().then(setVehicles).catch(() => {});
    adminStats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    adminMe()
      .then((u) => {
        setUser(u);
        load();
      })
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      });
  }, [navigate, load]);

  const handleDelete = async (v) => {
    if (!window.confirm(`Remove ${v.make} ${v.model} from stock? This cannot be undone.`)) return;
    try {
      await deleteVehicle(v.id);
      toast.success("Vehicle removed");
      load();
    } catch {
      toast.error("Could not delete vehicle");
    }
  };

  const handleQuote = async (v, term) => {
    try {
      await downloadQuotation(v.id, term);
      toast.success(`Quotation PDF downloaded (${term} months)`);
    } catch {
      toast.error("Could not generate quotation");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const statCards = [
    { icon: CarFront, label: "Vehicles in stock", value: stats?.vehicles ?? "–", testid: "stat-vehicles" },
    { icon: FileText, label: "Applications", value: stats?.applications ?? "–", testid: "stat-applications" },
    { icon: ClipboardCheck, label: "Pre-qualifications", value: stats?.prequalifications ?? "–", testid: "stat-prequalifications" },
    { icon: Mail, label: "Enquiries", value: stats?.enquiries ?? "–", testid: "stat-enquiries" },
  ];

  return (
    <div data-testid="admin-dashboard" className="pt-28 pb-24 min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-2">Administration</p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-white" data-testid="admin-page-title">{TAB_TITLES[tab] || "Administration"}</h1>
            {user && <p className="text-sm text-slate-400 mt-1" data-testid="admin-user-email">Signed in as {user.email}</p>}
          </div>
          <div className="flex gap-3">
            <button
              data-testid="export-vehicles-csv"
              onClick={() => downloadCsv("vehicles.csv", "compcar-vehicles.csv").catch(() => toast.error("Export failed"))}
              className="inline-flex items-center gap-2 border border-sky-300/30 text-slate-300 hover:text-white hover:border-sky-300 text-sm font-bold px-5 py-3 rounded-md transition-colors duration-300"
            >
              <FileDown size={16} /> Stock CSV
            </button>
            <button
              data-testid="stock-sheet-btn"
              onClick={() => downloadStockSheet().catch(() => toast.error("Could not generate stock sheet"))}
              className="inline-flex items-center gap-2 border border-sky-300/30 text-slate-300 hover:text-white hover:border-sky-300 text-sm font-bold px-5 py-3 rounded-md transition-colors duration-300"
            >
              <FileText size={16} /> Stock sheet PDF
            </button>
            <button
              data-testid="add-vehicle-btn"
              onClick={() => setModal({ mode: "add" })}
              className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white text-sm font-bold px-5 py-3 rounded-md transition-colors duration-300"
            >
              <Plus size={16} /> Add vehicle
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-sky-300/30 text-slate-300 hover:text-white hover:border-sky-300 text-sm font-bold px-5 py-3 rounded-md transition-colors duration-300"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {statCards.map((s) => (
            <div key={s.label} data-testid={s.testid} className="bg-[#122659] border border-sky-300/20 rounded-xl p-6">
              <s.icon size={22} className="text-[#F57C00] mb-3" />
              <p className="font-display text-3xl font-black text-white">{s.value}</p>
              <p className="text-xs text-slate-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-8" data-testid="admin-tabs">
          {[
            { key: "vehicles", label: "Vehicles" },
            { key: "leads", label: "Leads" },
            { key: "calendar", label: "Calendar" },
            { key: "reviews", label: "Reviews" },
            { key: "referrals", label: "Referrals" },
            { key: "insights", label: "Insights" },
            { key: "settings", label: "Settings" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`text-sm font-bold px-5 py-2.5 rounded-full border transition-colors duration-200 ${
                tab === t.key ? "bg-[#F57C00] border-[#F57C00] text-white" : "border-sky-300/30 text-slate-300 hover:border-sky-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && <LeadsTab />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "referrals" && <ReferralsTab />}
        {tab === "insights" && <InsightsTab />}
        {tab === "settings" && <SettingsTab />}

        {tab === "vehicles" && (
        <>
        <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vehicles-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-sky-300 border-b border-sky-300/10">
                  <th className="px-6 py-4">Vehicle</th>
                  <th className="px-6 py-4">Price</th>
                  <th className="px-6 py-4">From / day</th>
                  <th className="px-6 py-4">Available for</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} data-testid={`vehicle-row-${v.slug}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {v.image ? (
                          <img src={mediaUrl(v.image)} alt="" className="w-16 h-11 object-cover rounded-md border border-sky-300/20" />
                        ) : (
                          <div className="w-16 h-11 rounded-md bg-[#0B1F4E] border border-sky-300/20 flex items-center justify-center">
                            <CarFront size={18} className="text-slate-500" />
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-white">{v.make} {v.model}</p>
                          <p className="text-xs text-slate-500">{v.year} · {v.transmission} · {v.fuel}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-white whitespace-nowrap">{zar(v.price)}</td>
                    <td className="px-6 py-4 text-slate-300 whitespace-nowrap">R{v.day_rate}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(v.services || []).map((s) => (
                          <span key={s} className="text-[10px] uppercase tracking-wider font-bold text-sky-300 border border-sky-300/30 rounded-full px-2 py-0.5">
                            {SERVICE_LABELS[s] || s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        {[48, 60].map((term) => (
                          <button
                            key={term}
                            data-testid={`quote-vehicle-${v.slug}-${term}`}
                            onClick={() => handleQuote(v, term)}
                            title={`Download ${term}-month quotation PDF`}
                            className="inline-flex items-center gap-1 p-2.5 rounded-md border border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-white transition-colors text-[10px] font-bold"
                          >
                            <FileDown size={14} /> {term}m
                          </button>
                        ))}
                        <button
                          data-testid={`edit-vehicle-${v.slug}`}
                          onClick={() => setModal({ mode: "edit", vehicle: v })}
                          className="p-2.5 rounded-md border border-sky-300/30 text-sky-300 hover:bg-sky-300 hover:text-[#0B1F4E] transition-colors"
                          aria-label={`Edit ${v.make} ${v.model}`}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          data-testid={`delete-vehicle-${v.slug}`}
                          onClick={() => handleDelete(v)}
                          className="p-2.5 rounded-md border border-red-400/40 text-red-400 hover:bg-red-400 hover:text-white transition-colors"
                          aria-label={`Delete ${v.make} ${v.model}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {vehicles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400" data-testid="vehicles-empty-row">
                      No vehicles in stock yet — click "Add vehicle" to list your first one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-4">
          Changes go live on the public catalogue immediately.
        </p>
        </>
        )}
      </div>

      {modal && (
        <VehicleFormModal
          vehicle={modal.mode === "edit" ? modal.vehicle : null}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
